<template>
  <div class="resume-path">
    <shell>
      <template #appbar>
        <app-bar
          :show-back="screen !== 'done'"
          subtitle="Motor claim · Resume with reference"
          :step-label="stepLabel"
          @back="goBack"
        />
      </template>

      <template v-if="screen === 'request'">
        <h2 class="mc-h2">Request OTP to Continue</h2>
        <p class="mc-lede">
          Enter your claim details to receive a one-time code on the phone
          number on record.
        </p>

        <div class="mc-field">
          <label class="mc-lbl" for="resume-ref"
            >Claims Ref No <span class="mc-req">*</span></label
          >
          <input
            id="resume-ref"
            v-model="S.ref"
            type="text"
            autocapitalize="characters"
            placeholder="202610-FJHN46"
            class="mc-input"
            :class="{ 'is-invalid': showErrors && !refOk }"
          />
          <p v-if="showErrors && !refOk" class="mc-hint is-err">
            Enter your claims reference number.
          </p>
        </div>

        <div class="mc-field">
          <label class="mc-lbl" for="resume-reg"
            >Vehicle Reg No <span class="mc-req">*</span></label
          >
          <input
            id="resume-reg"
            type="text"
            autocapitalize="characters"
            placeholder="KDA 123A"
            class="mc-input"
            :class="{ 'is-invalid': showErrors && !regOk }"
            :value="S.reg"
            @input="S.reg = $event.target.value.toUpperCase()"
          />
          <p v-if="showErrors && !regOk" class="mc-hint is-err">
            Enter the vehicle registration number.
          </p>
        </div>

        <div class="mc-field">
          <label class="mc-lbl" for="resume-phone"
            >Phone No <span class="mc-req">*</span></label
          >
          <input
            id="resume-phone"
            v-model="S.phone"
            type="tel"
            inputmode="tel"
            placeholder="07XX XXX XXX"
            class="mc-input"
            :class="{ 'is-invalid': showErrors && !phoneOk }"
          />
          <p v-if="showErrors && !phoneOk" class="mc-hint is-err">
            Enter a valid phone number.
          </p>
        </div>
      </template>

      <template v-else-if="screen === 'otp'">
        <h2 class="mc-h2">Validate OTP</h2>
        <p class="mc-lede">
          Enter the one-time code we sent to <b>{{ S.phone }}</b
          >.
        </p>

        <div class="mc-field">
          <label class="mc-lbl" for="resume-otp"
            >OTP <span class="mc-req">*</span></label
          >
          <input
            id="resume-otp"
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            maxlength="6"
            placeholder="______"
            class="mc-input"
            :class="{ 'is-invalid': showErrors && !otpOk }"
            :value="S.otp"
            @input="S.otp = $event.target.value.replace(/\D/g, '')"
          />
          <p v-if="showErrors && !otpOk" class="mc-hint is-err">
            Enter the code to continue.
          </p>
        </div>
      </template>

      <template v-else-if="screen === 'documents'">
        <h2 class="mc-h2">Police Abstract Submission</h2>
        <p class="mc-lede">
          Attach a clear copy of your police abstract to finish resuming this
          claim.
        </p>
        <photo-doc-capture
          :items="DOCS"
          :captured="captured"
          @select="onCapture"
          @remove="onRemove"
          @error="(key, msg) => showToast(msg)"
        />
      </template>

      <done-step
        v-else-if="screen === 'done'"
        :reference="S.ref"
        :phone="S.phone"
      />

      <div
        v-if="errorMsg && screen !== 'done'"
        class="mc-card-error"
        data-testid="resume-error"
      >
        {{ errorMsg }}
      </div>

      <template #bar>
        <bottom-bar>
          <button
            v-if="screen === 'done'"
            type="button"
            class="mc-btn mc-btn--pri"
            @click="$emit('back')"
          >
            Done
          </button>
          <template v-else>
            <button type="button" class="mc-btn mc-btn--sec" @click="goBack">
              Back
            </button>
            <button
              type="button"
              class="mc-btn mc-btn--pri"
              :disabled="busy"
              @click="onPrimary"
            >
              {{ primaryLabel }}
            </button>
          </template>
        </bottom-bar>
      </template>

      <template #toast><toast ref="toast" /></template>
    </shell>
  </div>
</template>

<script>
import Shell from '../motor-claim/shell/Shell.vue'
import AppBar from '../motor-claim/shell/AppBar.vue'
import BottomBar from '../motor-claim/shell/BottomBar.vue'
import Toast from '../motor-claim/shell/Toast.vue'
import PhotoDocCapture from '../motor-claim/shared/PhotoDocCapture.vue'
import DoneStep from '../motor-claim/shared/DoneStep.vue'
import {
  generateResumeOtp,
  validateResumeOtp,
} from '@/services/resume-claim-service'
import { uploadClaimAttachment } from '@/services/claim-service'

const SCREEN_ORDER = ['request', 'otp', 'documents']
const STEP_OF = { request: 1, otp: 2, documents: 3, done: 0 }
const DOCS = [
  {
    key: 'abstract',
    label: 'Police Abstract',
    hint: 'Attach Police Abstract',
    required: true,
    accept: '.pdf,.doc,.docx,image/*',
  },
]

export default {
  name: 'ResumeClaimPath',
  components: { Shell, AppBar, BottomBar, Toast, PhotoDocCapture, DoneStep },
  emits: ['back'],
  props: {
    initialReference: { type: String, default: '' },
  },
  data() {
    return {
      screen: 'request',
      S: {
        ref: this.initialReference,
        reg: '',
        phone: '',
        otp: '',
        claimId: '',
      },
      abstract: null,
      DOCS,
      busy: false,
      showErrors: false,
      errorMsg: '',
    }
  },
  computed: {
    refOk() {
      return this.S.ref.trim().length >= 3
    },
    regOk() {
      return this.S.reg.trim().length >= 5
    },
    phoneOk() {
      return /\d{9,}/.test(this.S.phone.replace(/\D/g, ''))
    },
    otpOk() {
      return this.S.otp.trim().length >= 4
    },
    stepLabel() {
      return STEP_OF[this.screen] ? `Step ${STEP_OF[this.screen]} of 3` : ''
    },
    captured() {
      return this.abstract ? { abstract: { name: this.abstract.name } } : {}
    },
    primaryLabel() {
      if (this.screen === 'request')
        return this.busy ? 'Requesting…' : 'Request OTP'
      if (this.screen === 'otp') return this.busy ? 'Validating…' : 'Validate'
      return this.busy ? 'Submitting…' : 'Submit'
    },
  },
  methods: {
    showToast(message, type) {
      if (this.$refs.toast) this.$refs.toast.show(message, type)
    },
    goBack() {
      const i = SCREEN_ORDER.indexOf(this.screen)
      if (i <= 0) return this.$emit('back')
      this.showErrors = false
      this.errorMsg = ''
      this.screen = SCREEN_ORDER[i - 1]
    },
    onPrimary() {
      if (this.screen === 'request') return this.requestOtp()
      if (this.screen === 'otp') return this.validateOtp()
      return this.submitAbstract()
    },
    async requestOtp() {
      this.showErrors = true
      this.errorMsg = ''
      if (!(this.refOk && this.regOk && this.phoneOk)) return
      this.busy = true
      try {
        const { data } = await generateResumeOtp({
          journeyResumeReference: this.S.ref.trim(),
          regNo: this.S.reg.trim(),
          phoneNumber: this.S.phone.trim(),
        })
        if (!data || !data.success) {
          this.errorMsg =
            (data && data.msg) || 'Could not request a code. Please try again.'
          return
        }
        this.showToast(data.msg || 'OTP sent', 'success')
        this.showErrors = false
        this.screen = 'otp'
      } catch {
        this.errorMsg = 'Could not request a code. Please try again.'
      } finally {
        this.busy = false
      }
    },
    async validateOtp() {
      this.showErrors = true
      this.errorMsg = ''
      if (!this.otpOk) return
      this.busy = true
      try {
        const { data } = await validateResumeOtp({
          journeyResumeReference: this.S.ref.trim(),
          regNo: this.S.reg.trim(),
          phoneNumber: this.S.phone.trim(),
          otp: this.S.otp.trim(),
        })
        if (!data || !data.success) {
          this.errorMsg =
            (data && data.msg) || 'That code could not be verified.'
          return
        }
        this.S.claimId = (data.data && data.data.claimId) || ''
        this.showToast(data.msg || 'OTP verified', 'success')
        this.showErrors = false
        this.screen = 'documents'
      } catch {
        this.errorMsg = 'That code could not be verified. Please try again.'
      } finally {
        this.busy = false
      }
    },
    onCapture(key, file) {
      this.abstract = { name: file.name, file }
    },
    onRemove() {
      this.abstract = null
    },
    async submitAbstract() {
      this.showErrors = true
      if (!this.abstract) {
        this.showToast('Please attach the police abstract before submitting.')
        return
      }
      this.busy = true
      try {
        const { data } = await uploadClaimAttachment(
          this.abstract.file,
          this.S.reg.trim(),
          'abstract',
          this.S.claimId
        )
        if (!data || !data.success) {
          this.showToast(
            (data && data.msg) ||
              'Could not submit your police abstract. Please try again.'
          )
          return
        }
        this.screen = 'done'
      } catch {
        this.showToast(
          'Could not submit your police abstract. Please try again.'
        )
      } finally {
        this.busy = false
      }
    },
  },
}
</script>

<style scoped src="@/assets/css/resume-claim.css"></style>
